import { useState, useEffect, useRef, useCallback } from 'react';
import type { Panel as PanelType, DashboardLayout, LabeledListFieldValue } from '@/types/dashboard';
import type { ChartBackendData } from '@/types/chart';
import type { EntityListData } from '@/types/entity-list';
import { previewPanelData } from '@/utils/api';
import { GridContainer } from '@/components/GridContainer';

interface EditorPreviewProps {
  panel: PanelType;
  contextKey: string;
  layout: DashboardLayout;
  onDataReady?: (data: PanelType['data']) => void;
}

function extractFormulas(panel: PanelType): Record<string, string> {
  if (!panel.data) return {};
  if ('formulas' in panel.data) {
    return { ...(panel.data.formulas ?? {}) };
  }
  return {};
}

function DebugView({ view, onViewChange }: { view: 'preview' | 'raw' | 'transformed'; onViewChange: (v: 'preview' | 'raw' | 'transformed') => void }) {
  const tabs: { key: 'preview' | 'raw' | 'transformed'; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'raw', label: 'Raw' },
    { key: 'transformed', label: 'Transformed' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          className={`btn btn-sm ${view === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => onViewChange(tab.key)}
          type="button"
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function EditorPreview({ panel, contextKey, layout, onDataReady }: EditorPreviewProps) {
  // Chat widget manages its own data — render directly without backend preview
  if (panel.type === 'chat') {
    return (
      <div data-testid="editor-preview" className="preview-container">
        <GridContainer
          layout={layout}
          panels={[{ ...panel, _dataLoaded: true, x: 0, y: 0 }]}
          onLayoutChange={() => {}}
          onPanelVisible={() => {}}
          isEditable={true}
          compactType={null}
          noNegativeMargins={true}
        />
      </div>
    );
  }

  const [previewPanel, setPreviewPanel] = useState<PanelType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugData, setDebugData] = useState<{ raw: Record<string, unknown>; transformed: Record<string, unknown> } | null>(null);
  const [view, setView] = useState<'preview' | 'raw' | 'transformed'>('preview');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formulas = extractFormulas(panel);
  const formulasKey = JSON.stringify(formulas);
  const configKey = JSON.stringify(panel.config);

  const fetchPreview = useCallback(async () => {
    if (!contextKey) return;

    setError(null);

    try {
      const response = await previewPanelData({
        contextKey,
        panelType: panel.type,
        formulas,
        panelConfig: panel.config as Record<string, unknown>,
      });

      if (panel.type === 'chart') {
        const chartResult = response.data.values as ChartBackendData;
        const currentFormulas = extractFormulas(panel);
        const mergedData = { ...chartResult, formulas: currentFormulas };
        setPreviewPanel({ ...panel, data: mergedData, _dataLoaded: true });
        if (response._debug) {
          setDebugData(response._debug);
        } else {
          setDebugData(null);
        }
        onDataReady?.(mergedData);
        return;
      }

      if (panel.type === 'entity_list') {
        const entityListData = response.data.values as EntityListData;
        const mergedData = {
          ...panel.data,
          items: entityListData.items ?? [],
          genericConfig: entityListData.genericConfig,
          meta: entityListData.meta ?? { totalCount: 0 },
        };
        setPreviewPanel({ ...panel, data: mergedData as PanelType['data'], _dataLoaded: true });
        if (response._debug) {
          setDebugData(response._debug);
        } else {
          setDebugData(null);
        }
        onDataReady?.(mergedData as PanelType['data']);
        return;
      }

      const indicatorValues = response.data.values as Record<string, LabeledListFieldValue>;
      const mergedData = {
        ...panel.data,
        value: indicatorValues.main?.value,
        delta: indicatorValues.main?.delta,
        values: indicatorValues,
      };

      setPreviewPanel({ ...panel, data: mergedData, _dataLoaded: true });
      if (response._debug) {
        setDebugData(response._debug);
      } else {
        setDebugData(null);
      }
      onDataReady?.(mergedData);
    } catch (err) {
      setDebugData(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [contextKey, panel.type, formulasKey, configKey]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      fetchPreview();
    }, 500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [fetchPreview]);

  let previewState: PanelType;
  if (error) {
    previewState = { ...panel, _loadError: error, x: 0, y: 0 };
  } else if (previewPanel) {
    previewState = { ...previewPanel, title: panel.title, config: panel.config, x: 0, y: 0 };
  } else {
    previewState = { ...panel, _dataLoaded: true, x: 0, y: 0 };
  }

  return (
    <div data-testid="editor-preview" className="preview-container">
      {debugData && (
        <DebugView view={view} onViewChange={setView} />
      )}
      {debugData && view !== 'preview' ? (
        <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12, background: '#f8f9fa', padding: 12, borderRadius: 4 }}>
          {JSON.stringify(debugData[view === 'raw' ? 'raw' : 'transformed'], null, 2)}
        </pre>
      ) : (
        <GridContainer
          layout={layout}
          panels={[previewState]}
          onLayoutChange={() => {}}
          onPanelVisible={() => {}}
          isEditable={true}
          compactType={null}
          noNegativeMargins={true}
        />
      )}
    </div>
  );
}
